'use client';

import { useInvestorQuery } from '@/hooks/use-investor-query';
import { fetchInvestorLiquidity, fetchInvestorRisk } from './api';
import { investorAnalyticsKeys } from './keys';
import type { AnalyticsWindow } from './schemas';

/**
 * The backend recomputes and caches these responses for 60 seconds, so a
 * matching poll keeps every consumer continuously current without hammering
 * the API. The identity/chain gate, retry policy, and error backoff come
 * from useInvestorQuery.
 */
const INVESTOR_ANALYTICS_REFETCH_MS = 60 * 1000;

interface InvestorAnalyticsOptions {
  /** Extra gate composed with the identity/chain gates (default true). */
  enabled?: boolean;
  /**
   * Poll cadence override. Fixed-window table rows fan out many parallel
   * queries; polling each at the 60s default multiplies request volume
   * enough to trip the API rate limiter — they pass a slower cadence.
   */
  cadenceMs?: number;
}

export function useInvestorRisk(
  window: AnalyticsWindow,
  options: InvestorAnalyticsOptions = {}
) {
  return useInvestorQuery({
    key: ({ address, chainId }) =>
      investorAnalyticsKeys.risk(address, chainId, window),
    fetch: ({ address, chainId }, signal) =>
      fetchInvestorRisk(address, chainId, window, signal),
    enabled: options.enabled !== false,
    cadenceMs: options.cadenceMs ?? INVESTOR_ANALYTICS_REFETCH_MS
  });
}

export function useInvestorLiquidity(options: InvestorAnalyticsOptions = {}) {
  return useInvestorQuery({
    key: ({ address, chainId }) =>
      investorAnalyticsKeys.liquidity(address, chainId),
    fetch: ({ address, chainId }, signal) =>
      fetchInvestorLiquidity(address, chainId, signal),
    enabled: options.enabled !== false,
    cadenceMs: options.cadenceMs ?? INVESTOR_ANALYTICS_REFETCH_MS
  });
}
